const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;
const REQUEST_TIMEOUT_MS = 20_000;
const UPLOAD_TIMEOUT_MS = 90_000;

export class WorkOrderPhotoUploadError extends Error {
  constructor(message, code = 'UPLOAD_FAILED') {
    super(message);
    this.name = 'WorkOrderPhotoUploadError';
    this.code = code;
  }
}

const readBlobAsArrayBuffer = (blob) => {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Unable to read photo'));
    reader.readAsArrayBuffer(blob);
  });
};

export const materializePhotoFile = async (file) => {
  if (!file) throw new WorkOrderPhotoUploadError('No photo was selected.', 'NO_FILE');

  let bytes;
  try {
    bytes = await readBlobAsArrayBuffer(file);
  } catch {
    throw new WorkOrderPhotoUploadError(
      'This photo is not available on the phone yet. Open it in Photos, let it download, and try again.',
      'FILE_UNAVAILABLE',
    );
  }

  const size = bytes?.byteLength || 0;
  if (size <= 0) {
    throw new WorkOrderPhotoUploadError(
      'The phone returned an empty photo. Open it in Photos, let it finish downloading, and select it again.',
      'EMPTY_FILE',
    );
  }
  if (size > MAX_PHOTO_SIZE_BYTES) {
    throw new WorkOrderPhotoUploadError('This photo is larger than the 10MB limit.', 'FILE_TOO_LARGE');
  }

  return new File([bytes], file.name || `photo-${Date.now()}.jpg`, {
    type: file.type || 'image/jpeg',
    lastModified: file.lastModified || Date.now(),
  });
};

const fetchWithTimeout = async (url, options, timeoutMs = REQUEST_TIMEOUT_MS, externalSignal) => {
  if (externalSignal?.aborted) {
    throw new WorkOrderPhotoUploadError('The photo upload was canceled.', 'CANCELED');
  }
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (error?.name === 'AbortError') {
      if (externalSignal?.aborted && !timedOut) {
        throw new WorkOrderPhotoUploadError('The photo upload was canceled.', 'CANCELED');
      }
      throw new WorkOrderPhotoUploadError('The connection timed out. Please check your signal and retry.', 'TIMEOUT');
    }
    throw new WorkOrderPhotoUploadError('The network connection was interrupted. Please retry.', 'NETWORK');
  } finally {
    window.clearTimeout(timeout);
    externalSignal?.removeEventListener('abort', abortFromCaller);
  }
};

const responseErrorMessage = async (response, fallback) => {
  try {
    const payload = await response.json();
    if (typeof payload?.message === 'string') return payload.message;
    if (Array.isArray(payload?.message)) return payload.message.join(', ');
  } catch {
    // The fallback is more useful than a JSON parsing error.
  }
  return fallback;
};

const putPhotoWithProgress = (uploadUrl, headers, file, onProgress, signal) => new Promise((resolve, reject) => {
  const request = new XMLHttpRequest();
  const cancelRequest = () => request.abort();
  const finish = (callback) => {
    signal?.removeEventListener('abort', cancelRequest);
    callback();
  };
  request.open('PUT', uploadUrl, true);
  request.timeout = UPLOAD_TIMEOUT_MS;
  if (signal?.aborted) {
    reject(new WorkOrderPhotoUploadError('The photo upload was canceled.', 'CANCELED'));
    return;
  }
  signal?.addEventListener('abort', cancelRequest, { once: true });
  Object.entries(headers || {}).forEach(([key, value]) => request.setRequestHeader(key, value));
  request.upload.onprogress = (event) => {
    if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 80) + 10);
  };
  request.onload = () => {
    if (request.status >= 200 && request.status < 300) finish(resolve);
    else finish(() => reject(new WorkOrderPhotoUploadError(`Photo storage rejected the upload (${request.status}).`, 'S3_REJECTED')));
  };
  request.onerror = () => finish(() => reject(new WorkOrderPhotoUploadError('The network connection was interrupted. Please retry.', 'NETWORK')));
  request.ontimeout = () => finish(() => reject(new WorkOrderPhotoUploadError('The photo upload timed out. Please retry.', 'TIMEOUT')));
  request.onabort = () => finish(() => reject(new WorkOrderPhotoUploadError('The photo upload was canceled.', 'CANCELED')));
  request.send(file);
});

const performUploadAttempt = async ({ apiUrl, file, onProgress, signal }) => {
  onProgress?.(5, 'preparing');
  const uploadResponse = await fetchWithTimeout(`${apiUrl}/api/work-orders/get-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ fileName: file.name, fileType: file.type, fileSize: file.size }),
  }, REQUEST_TIMEOUT_MS, signal);
  if (!uploadResponse.ok) {
    throw new WorkOrderPhotoUploadError(
      await responseErrorMessage(uploadResponse, 'Unable to prepare the photo upload.'),
      'PREPARE_FAILED',
    );
  }

  const { uploadUrl, imageUrl, headers } = await uploadResponse.json();
  onProgress?.(10, 'uploading');
  await putPhotoWithProgress(uploadUrl, headers, file, (progress) => onProgress?.(progress, 'uploading'), signal);

  onProgress?.(92, 'verifying');
  const verifyResponse = await fetchWithTimeout(`${apiUrl}/api/work-orders/verify-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ imageUrl, expectedSize: file.size }),
  }, REQUEST_TIMEOUT_MS, signal);
  if (!verifyResponse.ok) {
    throw new WorkOrderPhotoUploadError(
      await responseErrorMessage(verifyResponse, 'The uploaded photo could not be verified.'),
      'VERIFY_FAILED',
    );
  }
  const verified = await verifyResponse.json();
  if (!verified?.verified || verified.size <= 0) {
    throw new WorkOrderPhotoUploadError('The uploaded photo was empty. Please retry it.', 'EMPTY_UPLOAD');
  }
  onProgress?.(100, 'complete');
  return verified.imageUrl;
};

export const uploadWorkOrderPhoto = async ({ apiUrl, file, onProgress, retries = 1, signal }) => {
  const materializedFile = await materializePhotoFile(file);
  if (signal?.aborted) {
    throw new WorkOrderPhotoUploadError('The photo upload was canceled.', 'CANCELED');
  }
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await performUploadAttempt({ apiUrl, file: materializedFile, onProgress, signal });
    } catch (error) {
      lastError = error;
      const retryable = ['NETWORK', 'TIMEOUT', 'VERIFY_FAILED', 'EMPTY_UPLOAD'].includes(error?.code);
      if (!retryable || attempt === retries) break;
      onProgress?.(0, 'retrying');
    }
  }
  throw lastError || new WorkOrderPhotoUploadError('The photo upload failed.');
};

export { MAX_PHOTO_SIZE_BYTES };
